import { Injectable, Injector } from '@angular/core';
import {
  deleteToken,
  getToken,
  isSupported,
  MessagePayload,
  Messaging,
  onMessage,
  Unsubscribe
} from '@angular/fire/messaging';
import { Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IDeviceRequest } from '../../models/device.model';
import { DeviceService } from '../device/device.service';
import { ToastService } from '../toast/toast.service';

const DEVICE_ID_KEY = 'deviceId';

@Injectable({
  providedIn: 'root'
})
export class MessagingService {

  private foregroundUnsubscribe?: Unsubscribe;
  private registering: boolean = false;

  constructor(
    private injector: Injector,
    private deviceService: DeviceService,
    public toastService: ToastService
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR register                              */
  /* -------------------------------------------------------------------------- */
  // region register
  /**
   * Completa el registro FCM si el permiso ya fue concedido previamente.
   * Se llama en el login (flujo automático). No solicita permiso al usuario.
   * Para solicitar permiso, usar requestPermissionAndRegister() desde un click.
   */
  async register() {

    if (this.registering) return;
    this.registering = true;

    try {

      if (!('serviceWorker' in navigator) || !('Notification' in window)) {
        console.log('[FCM] Push no soportado en este navegador');
        return;
      }

      const supported = await isSupported();

      if (!supported) {
        console.log('[FCM] Firebase Messaging no soportado');
        return;
      }

      // Solo completar registro si el permiso ya fue concedido
      if (Notification.permission !== 'granted') {
        console.log('[FCM] Permiso pendiente - usar requestPermissionAndRegister() desde un click');
        return;
      }

      await this.doRegister();

    } catch(error) {
      console.log('[FCM] Error al registrar:', error);
    } finally {
      this.registering = false;
    }

  }

  /* -------------------------------------------------------------------------- */
  /*                   ANCHOR requestPermissionAndRegister                      */
  /* -------------------------------------------------------------------------- */
  // region requestPermissionAndRegister
  /**
   * Solicita permiso al usuario y completa el registro FCM.
   * DEBE llamarse desde un evento de click del usuario (user gesture).
   * Retorna el estado del permiso: 'granted' | 'denied' | 'default'.
   */
  async requestPermissionAndRegister(): Promise<NotificationPermission> {

    try {

      if (!('serviceWorker' in navigator) || !('Notification' in window)) {
        return 'denied';
      }

      const supported = await isSupported().catch(() => false);
      if (!supported) return 'denied';

      if (Notification.permission === 'granted') {
        // Ya tiene permiso, solo asegurar que el token esté registrado
        if (!this.registering) {
          this.registering = true;
          try {
            await this.doRegister();
          } finally {
            this.registering = false;
          }
        }
        return 'granted';
      }

      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        return permission;
      }

      if (!this.registering) {
        this.registering = true;
        try {
          await this.doRegister();
        } finally {
          this.registering = false;
        }
      }

      return permission;

    } catch(error) {
      console.log('[FCM] Error al solicitar permiso:', error);
      return 'denied';
    }

  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR doRegister                               */
  /* -------------------------------------------------------------------------- */
  // region doRegister
  private async doRegister() {

    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js'
    );

    const messaging = this.injector.get(Messaging);

    const token = await getToken(
      messaging,
      {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: registration
      }
    );

    if (!token) {
      console.log('[FCM] No se pudo obtener el token');
      return;
    }

    console.log('[FCM] Token obtenido');

    await this.syncDevice(token);

    this.listenForegroundMessages(messaging);

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR syncDevice                              */
  /* -------------------------------------------------------------------------- */
  // region syncDevice
  private async syncDevice(token: string) {

    const deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (deviceId) {

      const device = await this.deviceService.get(deviceId);

      if (device) {

        if (device.token !== token) {

          await this.deviceService.update(
            deviceId,
            {
              os: device.os,
              brand: device.brand,
              model: device.model,
              description: device.description,
              token
            }
          );

        }

        return;
      }

      // El device ya no existe en el servidor: limpiar y re-crear
      localStorage.removeItem(DEVICE_ID_KEY);
    }

    const newDevice = await this.deviceService.create(
      this.buildDeviceData(token)
    );

    if (newDevice) {
      localStorage.setItem(DEVICE_ID_KEY, newDevice.deviceId);
    }

  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR unregister                             */
  /* -------------------------------------------------------------------------- */
  // region unregister
  async unregister() {

    try {

      if (this.foregroundUnsubscribe) {
        this.foregroundUnsubscribe();
        this.foregroundUnsubscribe = undefined;
      }

      const deviceId = localStorage.getItem(DEVICE_ID_KEY);

      if (deviceId) {
        await this.deviceService.delete(deviceId);
        localStorage.removeItem(DEVICE_ID_KEY);
      }

      const supported = await isSupported().catch(() => false);

      if (
        supported &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        const messaging = this.injector.get(Messaging);
        await deleteToken(messaging);
      }

    } catch(error) {
      console.log('[FCM] Error al eliminar registro:', error);
    }

  }

  /* -------------------------------------------------------------------------- */
  /*                      ANCHOR listenForegroundMessages                       */
  /* -------------------------------------------------------------------------- */
  // region listenForegroundMessages
  private listenForegroundMessages(messaging: Messaging) {

    if (this.foregroundUnsubscribe) {
      this.foregroundUnsubscribe();
    }

    this.foregroundUnsubscribe = onMessage(
      messaging,
      (payload: MessagePayload) => {

        console.log('[FCM] Mensaje en foreground:', payload);

        const notificationTypeCode = payload.data?.['notificationTypeCode'];

        const isSos = notificationTypeCode === 'sos';

        this.toastService.show(
          {
            header: payload.notification?.title ?? 'CradleGuard',
            body: payload.notification?.body ?? '',
            classname: isSos ? 'bg-danger text-white' : 'bg-dark text-white'
          }
        );

      }
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR buildDeviceData                           */
  /* -------------------------------------------------------------------------- */
  // region buildDeviceData
  private buildDeviceData(token: string): IDeviceRequest {

    const brand = this.getBrowserName();
    const model = this.getPlatformName();
    const version = this.getBrowserVersion(brand);

    const description = `${brand} ${version} - ${model}`
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 35);

    return {
      os: 'Web',
      brand,
      model,
      description,
      token
    };

  }

  private getBrowserName(): string {

    const ua = navigator.userAgent;

    if (/Edg\//.test(ua)) return 'Edge';
    if (/OPR\//.test(ua)) return 'Opera';
    if (/Chrome\//.test(ua)) return 'Chrome';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/Safari\//.test(ua)) return 'Safari';

    return 'Web';

  }

  private getBrowserVersion(browser: string): string {

    const tokens: { [key: string]: string } = {
      'Edge': 'Edg',
      'Opera': 'OPR',
      'Chrome': 'Chrome',
      'Firefox': 'Firefox',
      'Safari': 'Version'
    };

    const token = tokens[browser];

    if (!token) return '';

    const match = navigator.userAgent.match(
      new RegExp(`${token}\\/(\\d+)`)
    );

    return match ? match[1] : '';

  }

  private getPlatformName(): string {

    const ua = navigator.userAgent;

    if (/Android/.test(ua)) return 'Android';
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Mac/.test(ua)) return 'macOS';
    if (/Linux/.test(ua)) return 'Linux';

    return 'Web';

  }

}
